import { inject, Injectable } from '@angular/core';
import { Observable, map, of, switchMap } from 'rxjs';
import { DATABASE_NAME } from 'src/app/config/app.config';
import { STORE_DATABASE } from 'src/app/core/enums/storage/store-database.enum';
import { IApiTicket } from '../interfaces/ticket-api.interface';
import { IndexedDBRepository } from 'src/app/shared/interface/indexed-db-storage.model';
import { Ticket } from '../../domain/model/ticket.model';
import { TicketMapper } from '../mapper/ticket.mapper';
import { TicketFactoryService } from './ticket-factory.service';

@Injectable({ providedIn: 'root' })
export class TicketStorageService extends IndexedDBRepository<IApiTicket> {
  readonly dbName = DATABASE_NAME;
  readonly instanceName = STORE_DATABASE.tickets;
  private readonly ticketFactoryService = inject(TicketFactoryService);

  getAll(): Observable<Ticket[]> {
    return this.getItems().pipe(
      switchMap((items) => this.ticketFactoryService.createMany(items))
    );
  }

  getById(id: string): Observable<Ticket | null> {
    return this.getItem(id).pipe(
      switchMap((item) =>
        item ? this.ticketFactoryService.createOne(item, true) : of(null)
      )
    );
  }

  saveAll(tickets: Ticket[]): Observable<void> {
    if (!tickets.length) {
      return of(undefined);
    }

    const items = tickets.map(ticket => ({
      key: ticket?.id,
      value: TicketMapper.toJSON(ticket),
    }));

    return this.setItems(items).pipe(map(() => undefined));
  }

  save(ticket: Ticket): Observable<void> {
    return this.setItem(ticket?.id, TicketMapper.toJSON(ticket)).pipe(
      map(() => undefined)
    );
  }

  clear(): Observable<void> {
    return this.clearAll();
  }
}
